import { i18n } from "../i18n";
import type { Flip } from "../premises_census";
import type { NightClock } from "./night_clock";

export interface Person {
  name: string;
  bed: string;
}

interface Targets {
  query?: HTMLInputElement;
  clearQuery?: HTMLElement;
  shown: HTMLElement;
  empty: HTMLElement;
}

// The Who's here list: one server-rendered row per person, shown, hidden and
// ordered by their standing at the pointer, narrowed by the search box. Rows
// carry their name and bed as data, so a broadcast row joins with no lookup.
export class PeopleList {
  private rows = new Map<number, HTMLElement>();
  private sinceCells = new Map<number, HTMLElement | null>();
  private people = new Map<number, Person>();

  constructor(rows: HTMLElement[], private readonly targets: Targets, private readonly clock: NightClock) {
    rows.forEach((row) => this.add(row));
  }

  add(row: HTMLElement) {
    const id = Number(row.dataset.personId);
    this.rows.set(id, row);
    this.people.set(id, { name: row.dataset.name ?? "", bed: row.dataset.bed ?? "" });
  }

  personOf(id: number): Person | undefined {
    return this.people.get(id);
  }

  paint(flips: Flip[]) {
    flips.forEach((flip) => {
      const row = this.rows.get(flip.personId);
      if (row) this.paintRow(row, flip);
    });
  }

  paintAll(standingOf: (id: number) => Flip) {
    this.rows.forEach((row, id) => this.paintRow(row, standingOf(id)));
  }

  filter(isInside: (id: number) => boolean) {
    this.rows.forEach((row, id) => {
      row.hidden = !(isInside(id) && this.matches(row));
    });
    if (this.targets.clearQuery) this.targets.clearQuery.hidden = this.query() === "";
  }

  clearQuery() {
    if (!this.targets.query) return;
    this.targets.query.value = "";
    this.targets.query.focus();
  }

  // "n of m" beside the title while a query narrows the list, and the empty
  // line under it when nothing is left to show.
  count(inside: number) {
    const query = this.query();
    let shown = 0;
    this.rows.forEach((row) => { if (!row.hidden) shown += 1; });
    this.targets.shown.textContent = query ? i18n.t("premises.people.shown_of", { shown, inside }) : "";
    this.targets.empty.hidden = shown > 0;
    this.targets.empty.textContent = query
      ? i18n.t("premises.people.empty_query", { query: this.targets.query?.value ?? "" })
      : i18n.t("premises.people.empty_moment");
  }

  private paintRow(row: HTMLElement, flip: Flip) {
    const hidden = !(flip.inside && this.matches(row));
    if (row.hidden !== hidden) row.hidden = hidden;
    if (!flip.inside) return;
    const order = String(-Math.round((flip.since ?? 0) / 60));
    if (row.style.order !== order) row.style.order = order;
    let since = this.sinceCells.get(flip.personId);
    if (since === undefined) {
      since = row.querySelector<HTMLElement>("[data-role=since]");
      this.sinceCells.set(flip.personId, since);
    }
    if (since && flip.since !== null) since.textContent = this.clock.timeAt(flip.since);
  }

  private matches(row: HTMLElement): boolean {
    const query = this.query();
    return query === "" || (row.dataset.search || "").includes(query);
  }

  private query(): string {
    return this.targets.query?.value.trim().toLowerCase() ?? "";
  }
}
